import type { RestClient } from "./restClient";

export interface CompanyMember {
  user_id: string;
  email: string | null;
  joined_at: string;
}

export interface Paginated<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface CompanyInvite {
  code: string;
  email: string | null;
  expires_at: string;
}

export interface CompanySummary {
  id: string;
  name: string;
  member_count: number;
}

export interface OrgMembership {
  company_id: string;
  name: string;
  roles: string[];
  joined_at: string | null;
  active: boolean;
}

export interface OrgsResponse {
  orgs: OrgMembership[];
  active_company_id: string | null;
}

export class CompaniesApi {
  constructor(private client: RestClient) {}

  createCompany(name: string): Promise<{ id: string; name: string }> {
    return this.client.post("/v1/companies", { name });
  }

  join(code: string): Promise<{ id: string }> {
    return this.client.post("/v1/companies/join", { code });
  }

  me(): Promise<CompanySummary> {
    return this.client.get("/v1/companies/me");
  }

  async members(params?: {
    page?: number;
    limit?: number;
    q?: string;
  }): Promise<{
    members: CompanyMember[];
    pagination: Paginated<CompanyMember>["pagination"];
  }> {
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 25;
    const qs = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    const q = params?.q?.trim();
    if (q) qs.set("q", q);
    const raw = await this.client.get<{
      members?: CompanyMember[];
      pagination?: Paginated<CompanyMember>["pagination"];
    }>(`/v1/companies/me/members?${qs.toString()}`);
    const members = raw.members ?? [];
    const pagination = raw.pagination ?? {
      page,
      limit,
      total: members.length,
      total_pages: Math.max(1, Math.ceil(members.length / limit) || 1),
    };
    return { members, pagination };
  }

  /** Fetch every member (paginates internally). Use for small pickers. */
  async membersAll(): Promise<CompanyMember[]> {
    const all: CompanyMember[] = [];
    let page = 1;
    for (;;) {
      const res = await this.members({ page, limit: 100 });
      all.push(...res.members);
      if (page >= res.pagination.total_pages) break;
      page += 1;
    }
    return all;
  }

  createInvite(email?: string, ttlHours?: number): Promise<CompanyInvite> {
    return this.client.post("/v1/companies/me/invites", {
      email: email ?? null,
      ...(ttlHours ? { ttl_hours: ttlHours } : {}),
    });
  }

  invites(): Promise<{ invites: CompanyInvite[] }> {
    return this.client.get("/v1/companies/me/invites");
  }

  revokeInvite(code: string): Promise<{ ok: boolean }> {
    return this.client.delete(`/v1/companies/me/invites/${encodeURIComponent(code)}`);
  }

  removeMember(userId: string): Promise<{ ok: boolean }> {
    return this.client.delete(`/v1/companies/me/members/${encodeURIComponent(userId)}`);
  }

  // ── Multi-org (V27) ──
  orgs(): Promise<OrgsResponse> {
    return this.client.get("/v1/orgs");
  }

  switchOrg(companyId: string): Promise<{ ok: boolean; active_company_id: string }> {
    return this.client.post("/v1/orgs/switch", { company_id: companyId });
  }

  addMember(email: string, role?: string): Promise<{ user_id: string; email: string; role: string; provisioned: boolean }> {
    return this.client.post("/v1/companies/me/members", { email, role: role ?? "member" });
  }
}
